import React, { useState, useMemo, useEffect } from 'react';
import { sessionAPI } from '../../services/api';
import { tokenManager } from '../../services/tokenManager';

const Calendar = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [calendarView, setCalendarView] = useState('week'); // 'week' or 'month'
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewAllModal, setViewAllModal] = useState({ open: false, appointments: [], title: '' });

  // Skeleton loader components
  const CalendarHeaderSkeleton = () => (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
      <div>
        <div className="h-8 bg-gray-200 rounded w-32 mb-2 animate-pulse"></div>
        <div className="h-4 bg-gray-200 rounded w-48 animate-pulse"></div>
      </div>
    </div>
  );

  const CalendarNavigationSkeleton = () => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        {/* Left: Week/Month Toggle */}
        <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
          <div className="h-8 bg-gray-200 rounded w-16 animate-pulse"></div>
          <div className="h-8 bg-gray-200 rounded w-16 animate-pulse"></div>
        </div>

        {/* Center: Navigation */}
        <div className="flex items-center space-x-4">
          <div className="h-8 w-8 bg-gray-200 rounded animate-pulse"></div>
          <div className="h-6 bg-gray-200 rounded w-48 animate-pulse"></div>
          <div className="h-8 w-8 bg-gray-200 rounded animate-pulse"></div>
        </div>

        {/* Right: Filter Button */}
        <div className="h-8 bg-gray-200 rounded w-20 animate-pulse"></div>
      </div>
    </div>
  );

  const WeekViewSkeleton = () => (
    <div className="grid grid-cols-8 gap-px bg-gray-200 rounded-lg overflow-hidden">
      {/* Time column header */}
      <div className="bg-gray-50 p-3 text-center">
        <div className="h-4 bg-gray-200 rounded w-8 mx-auto animate-pulse"></div>
      </div>
      
      {/* Day headers */}
      {[...Array(7)].map((_, index) => (
        <div key={index} className="bg-gray-50 p-3 text-center">
          <div className="h-3 bg-gray-200 rounded w-8 mx-auto mb-1 animate-pulse"></div>
          <div className="h-4 bg-gray-200 rounded w-6 mx-auto animate-pulse"></div>
        </div>
      ))}
      
      {/* Time slots */}
      {[...Array(11)].map((_, timeIndex) => (
        <React.Fragment key={timeIndex}>
          {/* Time label */}
          <div className="bg-white p-3 flex items-start justify-center border-r border-gray-200">
            <div className="h-3 bg-gray-200 rounded w-8 animate-pulse"></div>
          </div>
          
          {/* Day cells */}
          {[...Array(7)].map((_, dateIndex) => (
            <div
              key={`${timeIndex}-${dateIndex}`}
              className="bg-white p-2 min-h-[90px] border-r border-gray-200"
            >
              {/* Random appointment skeletons */}
              {Math.random() > 0.7 && (
                <div className="space-y-1">
                  <div className="h-12 bg-gray-200 rounded animate-pulse"></div>
                  {Math.random() > 0.5 && (
                    <div className="h-8 bg-gray-200 rounded animate-pulse"></div>
                  )}
                </div>
              )}
            </div>
          ))}
        </React.Fragment>
      ))}
    </div>
  );

  const MonthViewSkeleton = () => (
    <div>
      {/* Month grid header */}
      <div className="grid grid-cols-7 gap-px bg-gray-200 rounded-t-lg overflow-hidden">
        {[...Array(7)].map((_, index) => (
          <div key={index} className="bg-gray-50 p-3 text-center">
            <div className="h-3 bg-gray-200 rounded w-8 mx-auto animate-pulse"></div>
          </div>
        ))}
      </div>

      {/* Month grid */}
      <div className="grid grid-cols-7 gap-px bg-gray-200 rounded-b-lg overflow-hidden">
        {[...Array(35)].map((_, index) => (
          <div
            key={index}
            className="bg-white p-3 min-h-[140px]"
          >
            <div className="h-4 bg-gray-200 rounded w-6 mb-3 animate-pulse"></div>
            <div className="space-y-1">
              {Math.random() > 0.6 && (
                <div className="h-8 bg-gray-200 rounded animate-pulse"></div>
              )}
              {Math.random() > 0.7 && (
                <div className="h-6 bg-gray-200 rounded animate-pulse"></div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // Calculate current week dates dynamically based on selectedDate
  const weekDates = useMemo(() => {
    const startOfWeek = new Date(selectedDate);
    const day = startOfWeek.getDay();
    const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1);
    startOfWeek.setDate(diff);
    
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      dates.push(date);
    }
    return dates;
  }, [selectedDate]);

  // Format date to YYYY-MM-DD for API
  const formatDateForAPI = (date) => {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  // Fetch appointments for the current view
  const fetchAppointments = async () => {
    setLoading(true);
    setError(null);
    
    try {
      let dateFrom, dateTo;
      
      if (calendarView === 'week') {
        dateFrom = formatDateForAPI(weekDates[0]);
        dateTo = formatDateForAPI(weekDates[6]);
      } else {
        // For month view, get the first and last day of the month
        const year = selectedDate.getFullYear();
        const month = selectedDate.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        dateFrom = formatDateForAPI(firstDay);
        dateTo = formatDateForAPI(lastDay);
      }

      // Get current user (doctor)
      const user = tokenManager.getUser();
      const doctorId = user?.id || user?._id;

     

      if (!doctorId) {
        setError('User not authenticated. Please log in again.');
        return;
      }

      const response = await sessionAPI.getCalendarSessions(dateFrom, dateTo, doctorId);
      
      
      
      if (response.success) {
        // Transform API data to match calendar format
        const transformedAppointments = response.data.sessions.map(session => ({
          id: session.id,
          time: session.startTime,
          duration: session.duration,
          date: new Date(session.date),
          patient: session.patient.name,
          type: session.type,
          status: session.status,
          color: getStatusColor(session.status),
          endTime: session.endTime,
          patientEmail: session.patient.email,
          patientImage: session.patient.profileImage
        }));
        
        console.log('Transformed appointments:', transformedAppointments);
        setAppointments(transformedAppointments);
      } else {
        setError(response.message || 'Failed to fetch appointments');
      }
    } catch (error) {
      console.error('Error fetching appointments:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      if (error.response?.status === 401) {
        setError('Authentication failed. Please log in again.');
      } else if (error.response?.status === 403) {
        setError('Access denied. You do not have permission to view this data.');
      } else if (error.response?.status === 404) {
        setError('Calendar data not found.');
      } else if (error.response?.status >= 500) {
        setError('Server error. Please try again later.');
      } else {
        setError('Failed to load appointments. Please check your connection and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch appointments when view or date changes
  useEffect(() => {
    fetchAppointments();
  }, [calendarView, selectedDate]);

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800 border-green-200';
      case 'scheduled': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'cancelled': return 'bg-red-100 text-red-800 border-red-200';
      case 'in_progress': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Generate time slots for the day (full hours only)
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 8; hour <= 18; hour++) {
      slots.push(`${hour.toString().padStart(2, '0')}:00`);
    }
    return slots;
  };

  // Get days for month view
  const getMonthDates = () => {
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    const endDate = new Date(lastDay);
    
    // Adjust to start from Monday
    const startDay = firstDay.getDay();
    if (startDay !== 1) {
      startDate.setDate(startDate.getDate() - (startDay === 0 ? 6 : startDay - 1));
    }
    
    // Adjust to end on Sunday
    const endDay = lastDay.getDay();
    if (endDay !== 0) {
      endDate.setDate(endDate.getDate() + (7 - endDay));
    }

    const dates = [];
    const currentDate = new Date(startDate);
    
    while (currentDate <= endDate) {
      dates.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return dates;
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const formatMonthYear = (date) => {
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      year: 'numeric' 
    });
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isCurrentMonth = (date) => {
    return date.getMonth() === selectedDate.getMonth();
  };

  const navigateDate = (direction) => {
    const newDate = new Date(selectedDate);
    if (calendarView === 'week') {
      newDate.setDate(newDate.getDate() + (direction * 7));
    } else {
      newDate.setMonth(newDate.getMonth() + direction);
    }
    setSelectedDate(newDate);
  };

  // Convert 12-hour time format to 24-hour format for comparison
  const convertTo24Hour = (time12h) => {
    if (!time12h) return '';
    
    // Handle formats like "9:45 AM", "11:00 AM", etc.
    const [time, period] = time12h.split(' ');
    let [hours, minutes] = time.split(':');
    
    hours = parseInt(hours);
    minutes = parseInt(minutes);
    
    if (period === 'PM' && hours !== 12) {
      hours += 12;
    } else if (period === 'AM' && hours === 12) {
      hours = 0;
    }
    
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  const getAppointmentForTimeSlot = (date, time) => {
    return appointments.find(apt => {
      const aptTime24h = convertTo24Hour(apt.time);
      return aptTime24h === time && apt.date.toDateString() === date.toDateString();
    });
  };

  const getAppointmentsForDate = (date) => {
    return appointments.filter(apt => 
      apt.date.toDateString() === date.toDateString()
    );
  };

  // Helper: check if appointment time falls within the slot's 1-hour window
  const isTimeInSlot = (aptTime, slotTime) => {
    if (!aptTime || !slotTime) return false;
    const [slotHour, slotMin] = slotTime.split(':').map(Number);
    const [aptHour, aptMin] = aptTime.split(':').map(Number);
    const slotMinutes = slotHour * 60 + slotMin;
    const aptMinutes = aptHour * 60 + aptMin;
    return aptMinutes >= slotMinutes && aptMinutes < slotMinutes + 60;
  };

  // Normalize all appointment times and dates before rendering
  const normalizedAppointments = appointments.map(apt => ({
    ...apt,
    time24: convertTo24Hour(apt.time),
    dateObj: apt.date instanceof Date ? apt.date : new Date(apt.date)
  }));

  // For debugging: collect unmatched appointments
  const matchedAppointmentIds = new Set();

  const timeSlots = generateTimeSlots();
  const monthDates = getMonthDates();

  // Modal component
  const ViewAllModal = ({ open, appointments, title, onClose }) => {
    if (!open) return null;
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30 p-4">
        <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6 w-full max-w-sm sm:max-w-md relative">
          <button
            className="absolute top-2 right-2 text-gray-400 hover:text-gray-700 text-xl font-bold p-1"
            onClick={onClose}
            aria-label="Close"
          >
            &times;
          </button>
          <h2 className="text-base sm:text-lg font-semibold mb-4 text-center pr-6">{title}</h2>
          <div className="space-y-3 max-h-64 sm:max-h-96 overflow-y-auto">
            {appointments.map((appointment) => (
              <div
                key={appointment.id}
                className="rounded-lg shadow bg-blue-100 text-blue-900 p-3 border-l-4 border-blue-400 flex flex-col"
              >
                <div className="font-semibold text-xs sm:text-sm truncate">{appointment.patient}</div>
                <div className="text-xs opacity-80 truncate">{appointment.type}</div>
                <div className="text-xs mt-1">{appointment.time}</div>
                <div className="text-xs mt-1">{appointment.duration}min</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <CalendarHeaderSkeleton />
        <CalendarNavigationSkeleton />
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6">
            {calendarView === 'week' ? <WeekViewSkeleton /> : <MonthViewSkeleton />}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">Calendar</h1>
          <p className="text-sm sm:text-base text-gray-600">Manage your appointments and schedule</p>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error loading appointments</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
            </div>
          </div>
        </div>
      )}

      {/* Calendar Navigation */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="flex flex-col sm:flex-row items-center justify-between p-3 sm:p-4 border-b border-gray-200 gap-3 sm:gap-0">
          {/* Left: Week/Month Toggle */}
          <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg w-full sm:w-auto">
            <button
              onClick={() => setCalendarView('week')}
              className={`px-3 sm:px-4 py-2 text-sm font-medium rounded-md transition-colors flex-1 sm:flex-none ${
                calendarView === 'week'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Week
            </button>
            <button
              onClick={() => setCalendarView('month')}
              className={`px-3 sm:px-4 py-2 text-sm font-medium rounded-md transition-colors flex-1 sm:flex-none ${
                calendarView === 'month'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Month
            </button>
          </div>

          {/* Center: Navigation */}
          <div className="flex items-center space-x-2 sm:space-x-4">
            <button
              onClick={() => navigateDate(-1)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            
            <div className="text-base sm:text-lg font-semibold text-gray-900 min-w-[160px] sm:min-w-[200px] text-center">
              {calendarView === 'week' 
                ? `${formatDate(weekDates[0])} - ${formatDate(weekDates[6])}`
                : formatMonthYear(selectedDate)
              }
            </div>
            
            <button
              onClick={() => navigateDate(1)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* Right: Filter Button */}
          <div className="flex items-center gap-3 w-full sm:w-auto">
            <button className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors w-full sm:w-auto justify-center">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              Filter
            </button>
          </div>
        </div>

        {/* Calendar Content */}
        <div className="overflow-x-auto overflow-y-auto max-h-[70vh] sm:overflow-visible sm:max-h-none p-0">
          {calendarView === 'week' ? (
            <>
              <div className="grid grid-cols-8 gap-px bg-gray-200 rounded-lg min-w-[800px] sm:min-w-0 w-full">
                {/* Time column header */}
                <div className="bg-gray-50 p-2 sm:p-3 text-center min-w-[80px] sm:min-w-[110px]">
                  <span className="text-xs font-medium text-gray-500">Time</span>
                </div>
                {/* Day headers */}
                {weekDates.map((date, index) => (
                  <div
                    key={index}
                    className={`bg-gray-50 p-2 sm:p-3 text-center min-w-[80px] sm:min-w-[110px] ${
                      isToday(date) ? 'bg-blue-50 border-b-2 border-blue-500' : ''
                    }`}
                  >
                    <div className="text-xs font-medium text-gray-500">
                      {date.toLocaleDateString('en-US', { weekday: 'short' })}
                    </div>
                    <div className={`text-sm font-semibold mt-1 ${
                      isToday(date) ? 'text-blue-600' : 'text-gray-900'
                    }`}>
                      {date.getDate()}
                    </div>
                  </div>
                ))}
                {/* Time slots */}
                {timeSlots.map((time) => (
                  <React.Fragment key={time}>
                    {/* Time label */}
                    <div className="bg-white p-2 sm:p-3 flex items-start justify-center border-r border-gray-200 min-w-[80px] sm:min-w-[110px]">
                      <span className="text-xs text-gray-500">{time}</span>
                    </div>
                    {/* Day cells */}
                    {weekDates.map((date, dateIndex) => {
                      const appointmentsForSlot = normalizedAppointments.filter(
                        apt =>
                          isTimeInSlot(apt.time24, time) &&
                          apt.dateObj.toDateString() === date.toDateString()
                      );
                      appointmentsForSlot.forEach(apt => matchedAppointmentIds.add(apt.id));
                      const showViewAll = appointmentsForSlot.length > 2;
                      const visibleAppointments = showViewAll ? appointmentsForSlot.slice(0, 2) : appointmentsForSlot;
                      return (
                        <div
                          key={`${date.toDateString()}-${time}`}
                          className="bg-white p-1 sm:p-2 min-h-[80px] sm:min-h-[90px] border-r border-gray-200 min-w-[80px] sm:min-w-[110px]"
                        >
                          {visibleAppointments.map(appointment => (
                            <div
                              key={appointment.id}
                              className={`rounded-lg shadow bg-blue-100 text-blue-900 p-1 sm:p-2 mb-1 border-l-4 border-blue-400 flex flex-col`}
                              style={{ minWidth: '60px', maxWidth: '100%' }}
                            >
                              <div className="font-semibold text-xs truncate">{appointment.patient}</div>
                              <div className="text-xs opacity-80 truncate">{appointment.type}</div>
                              <div className="text-xs mt-1">{appointment.time}</div>
                              <div className="text-xs mt-1">{appointment.duration}min</div>
                            </div>
                          ))}
                          {showViewAll && (
                            <button
                              className="text-xs text-blue-600 underline mt-1 w-full text-left"
                              onClick={() => setViewAllModal({
                                open: true,
                                appointments: appointmentsForSlot,
                                title: `${date.toLocaleDateString()} ${time}`
                              })}
                            >
                              View All
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </React.Fragment>
                ))}
                {/* Debug: Show unmatched appointments in console */}
                {(() => {
                  const unmatched = normalizedAppointments.filter(apt => !matchedAppointmentIds.has(apt.id));
                  if (unmatched.length > 0) {
                    // eslint-disable-next-line no-console
                    console.warn('Unmatched appointments (not shown in week view):', unmatched);
                  }
                  return null;
                })()}
              </div>
            </>
          ) : (
            <>
              <div className="grid grid-cols-7 gap-px bg-gray-200 rounded-t-lg overflow-hidden min-w-[700px] sm:min-w-0 w-full">
                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
                  <div key={day} className="bg-gray-50 p-2 sm:p-3 text-center min-w-[70px] sm:min-w-[90px]">
                    <span className="text-xs font-medium text-gray-500">{day}</span>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-7 gap-px bg-gray-200 rounded-b-lg overflow-hidden min-w-[700px] sm:min-w-0 w-full">
                {Array.from({ length: Math.ceil(monthDates.length / 7) }, (_, weekIndex) =>
                  monthDates.slice(weekIndex * 7, (weekIndex + 1) * 7).map((date, dayIndex) => {
                    const dayAppointments = getAppointmentsForDate(date);
                    const showViewAll = dayAppointments.length > 2;
                    const visibleAppointments = showViewAll ? dayAppointments.slice(0, 2) : dayAppointments;
                    return (
                      <div
                        key={`${weekIndex}-${dayIndex}`}
                        className={`bg-white p-2 sm:p-3 min-h-[120px] sm:min-h-[140px] hover:bg-gray-50 transition-colors cursor-pointer min-w-[70px] sm:min-w-[90px] ${
                          !isCurrentMonth(date) ? 'bg-gray-50 text-gray-400' : ''
                        } ${
                          isToday(date) ? 'bg-blue-50 border-2 border-blue-500' : ''
                        }`}
                      >
                        <div className={`text-sm font-semibold mb-2 sm:mb-3 ${
                          isToday(date) ? 'text-blue-600' : 
                          isCurrentMonth(date) ? 'text-gray-900' : 'text-gray-400'
                        }`}>
                          {date.getDate()}
                        </div>
                        {/* Appointments for this date */}
                        <div className="space-y-1">
                          {visibleAppointments.map((appointment) => (
                            <div
                              key={appointment.id}
                              className={`text-xs p-1 sm:p-2 rounded-lg font-medium border-l-4 bg-blue-50 text-blue-800 border-blue-400`}
                            >
                              <div className="font-semibold text-xs">{appointment.time}</div>
                              <div className="font-medium truncate">{appointment.patient}</div>
                              <div className="text-xs opacity-75 truncate mt-1">{appointment.type}</div>
                              <div className="text-xs mt-1">{appointment.duration}min</div>
                            </div>
                          ))}
                          {showViewAll && (
                            <button
                              className="text-xs text-blue-600 underline mt-1 w-full text-left"
                              onClick={() => setViewAllModal({
                                open: true,
                                appointments: dayAppointments,
                                title: `${date.toLocaleDateString()}`
                              })}
                            >
                              View All
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </>
          )}
        </div>
      </div>
      {/* View All Modal */}
      <ViewAllModal
        open={viewAllModal.open}
        appointments={viewAllModal.appointments}
        title={viewAllModal.title}
        onClose={() => setViewAllModal({ open: false, appointments: [], title: '' })}
      />
    </div>
  );
};

export default Calendar;